/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import type { CleanedBlobRes } from "@cosmos/common";

/**
 * Test data for BlobTable component stories and testing
 *
 * This file contains realistic test data that matches the CleanedBlobRes interface,
 * based on actual download scenarios from Epic Games products like Unreal Engine,
 * Twinmotion, plugins, and sample projects.
 *
 * Available datasets:
 * - unrealEngineBlobs: Large engine downloads (27-30 GB) for different OS
 * - twinmotionBlobs: Application downloads (4-5 GB) for Windows/Mac
 * - pluginBlobs: Plugin downloads (512 MB - 2 GB) platform-agnostic
 * - sampleAssetsBlobs: Sample project downloads (8-14 GB) for learning
 * - singleBlob: Single small file for minimal testing
 * - emptyBlobs: Empty array for testing empty states
 */

export const unrealEngineBlobs: CleanedBlobRes[] = [
	{
		name: "Linux_Unreal_Engine_5.6.1.zip",
		createdAt: "2025-08-11T14:30:00.000Z",
		size: 30861852672, // 28.75 GB
		version: "5.6.1",
		semver: "5.6.1",
		operatingSystem: "linux",
		downloadUrl: "https://cdn.unrealengine.com/builds/linux/5.6.1/Linux_Unreal_Engine_5.6.1.zip",
	},
	{
		name: "Linux_Unreal_Engine_5.6.0_preview-1.zip",
		createdAt: "2025-05-12T09:15:00.000Z",
		size: 31800066048, // 29.62 GB
		version: "5.6.0-preview-1",
		semver: "5.6.0-preview-1",
		operatingSystem: "linux",
		downloadUrl: "https://cdn.unrealengine.com/builds/linux/5.6.0-preview-1/Linux_Unreal_Engine_5.6.0_preview-1.zip",
	},
	{
		name: "Windows_Unreal_Engine_5.6.1.zip",
		createdAt: "2025-08-11T14:30:00.000Z",
		size: 32212254720, // 30.0 GB
		version: "5.6.1",
		semver: "5.6.1",
		operatingSystem: "windows",
		downloadUrl: "https://cdn.unrealengine.com/builds/windows/5.6.1/Windows_Unreal_Engine_5.6.1.zip",
	},
	{
		name: "macOS_Unreal_Engine_5.6.1.dmg",
		createdAt: "2025-08-11T14:30:00.000Z",
		size: 29360128000, // 27.35 GB
		version: "5.6.1",
		semver: "5.6.1",
		operatingSystem: "macos",
		downloadUrl: "https://cdn.unrealengine.com/builds/macos/5.6.1/macOS_Unreal_Engine_5.6.1.dmg",
	},
	{
		name: "Linux_Unreal_Engine_5.5.4.zip",
		createdAt: "2025-03-22T11:45:00.000Z",
		size: 29896089600, // 27.85 GB
		version: "5.5.4",
		semver: "5.5.4",
		operatingSystem: "linux",
		downloadUrl: "https://cdn.unrealengine.com/builds/linux/5.5.4/Linux_Unreal_Engine_5.5.4.zip",
	},
];

// Single blob for testing edge cases
export const singleBlob: CleanedBlobRes[] = [
	{
		name: "UE5_QuickStart_Guide.pdf",
		createdAt: "2025-01-15T08:00:00.000Z",
		size: 10485760, // 10 MB
		version: "1.0.0",
		semver: "1.0.0",
		operatingSystem: "",
		downloadUrl: "https://cdn.unrealengine.com/docs/UE5_QuickStart_Guide.pdf",
	},
];

// Empty array for testing empty state
export const emptyBlobs: CleanedBlobRes[] = [];

// Test data with invalid download URLs for testing validation
export const blobsWithInvalidUrls: CleanedBlobRes[] = [
	{
		name: "Valid_Download.zip",
		createdAt: "2025-01-15T08:00:00.000Z",
		size: 10485760, // 10 MB
		version: "1.0.0",
		semver: "1.0.0",
		operatingSystem: "",
		downloadUrl: "https://cdn.unrealengine.com/valid/Valid_Download.zip",
	},
	{
		name: "Invalid_URL_Download.zip",
		createdAt: "2025-01-15T08:00:00.000Z",
		size: 15728640, // 15 MB
		version: "1.0.1",
		semver: "1.0.1",
		operatingSystem: "",
		downloadUrl: "not-a-valid-url",
	},
	{
		name: "Empty_URL_Download.zip",
		createdAt: "2025-01-15T08:00:00.000Z",
		size: 20971520, // 20 MB
		version: "1.0.2",
		semver: "1.0.2",
		operatingSystem: "",
		downloadUrl: "",
	},
	{
		name: "FTP_Protocol_Download.zip",
		createdAt: "2025-01-15T08:00:00.000Z",
		size: 5242880, // 5 MB
		version: "1.0.3",
		semver: "1.0.3",
		operatingSystem: "",
		downloadUrl: "ftp://ftp.example.com/file.zip",
	},
];

// Additional test data for various scenarios
export const twinmotionBlobs: CleanedBlobRes[] = [
	{
		name: "Twinmotion_2025.1_Win.exe",
		createdAt: "2025-07-15T16:20:00.000Z",
		size: 4831838208, // 4.5 GB
		version: "2025.1",
		semver: "2025.1.0",
		operatingSystem: "windows",
		downloadUrl: "https://cdn.twinmotion.com/builds/windows/2025.1/Twinmotion_2025.1_Win.exe",
	},
	{
		name: "Twinmotion_2025.1_Mac.dmg",
		createdAt: "2025-07-15T16:20:00.000Z",
		size: 5368709120, // 5.0 GB
		version: "2025.1",
		semver: "2025.1.0",
		operatingSystem: "macos",
		downloadUrl: "https://cdn.twinmotion.com/builds/macos/2025.1/Twinmotion_2025.1_Mac.dmg",
	},
];

export const pluginBlobs: CleanedBlobRes[] = [
	{
		name: "MetaHuman_Plugin_5.6.zip",
		createdAt: "2025-06-20T10:00:00.000Z",
		size: 2147483648, // 2.0 GB
		version: "5.6.0",
		semver: "5.6.0",
		operatingSystem: "",
		downloadUrl: "https://cdn.unrealengine.com/plugins/metahuman/5.6/MetaHuman_Plugin_5.6.zip",
	},
	{
		name: "Lumen_GI_Plugin_5.6.zip",
		createdAt: "2025-06-15T14:45:00.000Z",
		size: 536870912, // 512 MB
		version: "5.6.0",
		semver: "5.6.0",
		operatingSystem: "",
		downloadUrl: "https://cdn.unrealengine.com/plugins/lumen/5.6/Lumen_GI_Plugin_5.6.zip",
	},
];

export const sampleAssetsBlobs: CleanedBlobRes[] = [
	{
		name: "Valley_of_the_Ancient_Sample.zip",
		createdAt: "2025-01-10T12:00:00.000Z",
		size: 15032385536, // 14.0 GB
		version: "1.0.0",
		semver: "1.0.0",
		operatingSystem: "",
		downloadUrl: "https://cdn.unrealengine.com/samples/valley_ancient/Valley_of_the_Ancient_Sample.zip",
	},
	{
		name: "ActionRPG_Sample_Project.zip",
		createdAt: "2024-11-25T15:30:00.000Z",
		size: 8589934592, // 8.0 GB
		version: "4.27.0",
		semver: "4.27.0",
		operatingSystem: "",
		downloadUrl: "https://cdn.unrealengine.com/samples/actionrpg/ActionRPG_Sample_Project.zip",
	},
];

// Test data for section component stories with proper GraphQL types
export const mockSectionConfigs = {
	basicConfig: {
		__typename: "CosmosConfigSection" as const,
		heading: {
			__typename: "CosmosElementSectionHeading" as const,
			title: {
				text: "Download Unreal Engine",
				html: "Download Unreal Engine",
			},
			desc: "Get the latest version of Unreal Engine for your platform",
			buttons: [],
		},
		backgroundAnimation: null,
		backgroundImage: null,
		backgroundFullHeight: false,
		edsBackgroundColor: null,
	},

	noHeadingConfig: {
		__typename: "CosmosConfigSection" as const,
		heading: null,
		backgroundAnimation: null,
		backgroundImage: null,
		backgroundFullHeight: false,
		edsBackgroundColor: null,
	},
};
